/**
 * Gantt-style day view for venue availability, blocks, bookings, and tournament matches.
 * Rows = courts (grouped by venue). X-axis = time of day (e.g. 6:00–24:00).
 * Segments: availability (green), blocks (red), venue bookings (blue), tournament matches (purple).
 */

import React, { useMemo } from 'react';
import { getDateKeyInTimezone, formatTimeInTimezone, toUtcMs, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import './VenueGantt.css';

const HOUR_START = 6;
const HOUR_END = 24;
const MINUTES_PER_DAY = (HOUR_END - HOUR_START) * 60;
const DEFAULT_MATCH_DURATION_MINS = 30;

function toMinutes(str) {
  if (!str || typeof str !== 'string') return 0;
  const [h, m] = str.trim().split(':').map((n) => parseInt(n, 10) || 0);
  return h * 60 + m;
}

function toDate(val) {
  const ms = toUtcMs(val);
  return ms != null ? new Date(ms) : null;
}

/** dayOfWeek in data: 1=Mon .. 7=Sun. JS getDay(): 0=Sun, 1=Mon .. 6=Sat. */
function dayOfWeekForDate(date) {
  const d = date.getDay();
  return d === 0 ? 7 : d;
}

/** Build rows: { venueId, venueName, courtId, courtName } for all courts. */
function buildRows(venues, venueIdFilter) {
  const rows = [];
  const list = venueIdFilter ? venues.filter((v) => v.id === venueIdFilter) : venues;
  list.forEach((venue) => {
    const courts = venue.courts || [];
    if (courts.length === 0) {
      rows.push({ venueId: venue.id, venueName: venue.name || venue.id, courtId: null, courtName: '— No courts —' });
    } else {
      courts.forEach((c) => {
        rows.push({
          venueId: venue.id,
          venueName: venue.name || venue.id,
          courtId: c.id,
          courtName: c.name || c.id,
        });
      });
    }
  });
  return rows;
}

/** Segments for one row for the selected date. */
function segmentsForRow(row, selectedDate, availabilitySlots, blocks, bookings, matches, matchDurationMins) {
  const dateKey = getDateKeyInTimezone(selectedDate instanceof Date ? selectedDate.getTime() : selectedDate, DEFAULT_TIMEZONE);
  const dayOfWeek = dayOfWeekForDate(selectedDate);
  const segments = [];

  // Availability (recurring weekly). dayOfWeek 1=Mon..7=Sun; legacy 0 treated as Sun (7)
  const slots = availabilitySlots || [];
  slots.forEach((slot) => {
    const slotDay = slot.dayOfWeek === 0 ? 7 : slot.dayOfWeek;
    if (slotDay !== dayOfWeek) return;
    const startMins = toMinutes(slot.startTime) - HOUR_START * 60;
    const endMins = toMinutes(slot.endTime) - HOUR_START * 60;
    if (startMins >= MINUTES_PER_DAY || endMins <= 0) return;
    const left = Math.max(0, startMins) / MINUTES_PER_DAY * 100;
    const right = Math.min(MINUTES_PER_DAY, endMins) / MINUTES_PER_DAY * 100;
    segments.push({ type: 'availability', left, width: right - left, label: 'Available' });
  });

  // Blocks
  blocks.forEach((b) => {
    if (b.venueId !== row.venueId) return;
    if (b.courtId != null && b.courtId !== '' && b.courtId !== row.courtId) return;
    const start = toDate(b.startTime);
    const end = toDate(b.endTime);
    if (!start || getDateKeyInTimezone(toUtcMs(start), DEFAULT_TIMEZONE) !== dateKey) return;
    const dayStart = new Date(selectedDate);
    dayStart.setHours(HOUR_START, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(HOUR_END, 0, 0, 0);
    const startMins = (start.getTime() - dayStart.getTime()) / 60000;
    const endMins = (end.getTime() - dayStart.getTime()) / 60000;
    if (endMins <= 0 || startMins >= MINUTES_PER_DAY) return;
    const left = Math.max(0, startMins) / MINUTES_PER_DAY * 100;
    const right = Math.min(MINUTES_PER_DAY, endMins) / MINUTES_PER_DAY * 100;
    segments.push({ type: 'block', left, width: right - left, label: b.reason || 'Blocked', id: b.id });
  });

  // Venue bookings
  bookings.forEach((b) => {
    if (b.venueId !== row.venueId || b.courtId !== row.courtId) return;
    const start = toDate(b.startTime);
    const end = toDate(b.endTime);
    if (!start || getDateKeyInTimezone(toUtcMs(start), DEFAULT_TIMEZONE) !== dateKey) return;
    const dayStart = new Date(selectedDate);
    dayStart.setHours(HOUR_START, 0, 0, 0);
    const startMins = (start.getTime() - dayStart.getTime()) / 60000;
    const endMins = (end.getTime() - dayStart.getTime()) / 60000;
    if (endMins <= 0 || startMins >= MINUTES_PER_DAY) return;
    const left = Math.max(0, startMins) / MINUTES_PER_DAY * 100;
    const right = Math.min(MINUTES_PER_DAY, endMins) / MINUTES_PER_DAY * 100;
    segments.push({ type: 'booking', left, width: right - left, label: b.note || 'Booking', id: b.id });
  });

  // Tournament matches
  matches.forEach((m) => {
    if (m.venueId !== row.venueId || m.courtId !== row.courtId) return;
    const mDate = toDate(m.scheduledAt ?? m.scheduledDate);
    if (!mDate || getDateKeyInTimezone(mDate.getTime(), DEFAULT_TIMEZONE) !== dateKey) return;
    const timeStr = (m.scheduledTime && m.scheduledTime.trim()) || (m.scheduledAt != null || m.scheduledDate != null ? formatTimeInTimezone(m.scheduledAt ?? m.scheduledDate, DEFAULT_TIMEZONE) : '');
    const startMins = toMinutes(timeStr) - HOUR_START * 60;
    const endMins = startMins + (matchDurationMins ?? DEFAULT_MATCH_DURATION_MINS);
    if (endMins <= 0 || startMins >= MINUTES_PER_DAY) return;
    const left = Math.max(0, startMins) / MINUTES_PER_DAY * 100;
    const right = Math.min(MINUTES_PER_DAY, endMins) / MINUTES_PER_DAY * 100;
    segments.push({
      type: 'match',
      left,
      width: right - left,
      label: m.tournamentName || m.tournamentId || 'Match',
      id: m.id,
      matchNumber: m.matchNumber,
    });
  });

  return segments;
}

export default function VenueGantt({
  venues = [],
  blocks = [],
  bookings = [],
  matches = [],
  tournaments = [],
  selectedDate,
  venueIdFilter = '',
  matchDurationMins = DEFAULT_MATCH_DURATION_MINS,
}) {
  const date = selectedDate ? new Date(selectedDate) : new Date();

  const rows = useMemo(() => buildRows(venues, venueIdFilter || null), [venues, venueIdFilter]);

  const matchesWithTournamentName = useMemo(() => {
    const tById = Object.fromEntries((tournaments || []).map((t) => [t.id, t]));
    return (matches || []).map((m) => ({
      ...m,
      tournamentName: tById[m.tournamentId]?.name || m.tournamentId,
    }));
  }, [matches, tournaments]);

  const hourLabels = [];
  for (let h = HOUR_START; h < HOUR_END; h++) {
    hourLabels.push(`${h}:00`);
  }

  return (
    <div className="venue-gantt">
      <div className="venue-gantt-legend">
        <span className="venue-gantt-legend-item availability">Available</span>
        <span className="venue-gantt-legend-item block">Blocked</span>
        <span className="venue-gantt-legend-item booking">Booking</span>
        <span className="venue-gantt-legend-item match">Tournament match</span>
      </div>
      <div className="venue-gantt-scroll">
        <table className="venue-gantt-table">
          <thead>
            <tr>
              <th className="venue-gantt-row-label">Venue / Court</th>
              <th colSpan={hourLabels.length} className="venue-gantt-time-axis">
                <div className="venue-gantt-time-labels">
                  {hourLabels.map((label, i) => (
                    <span key={i} className="venue-gantt-time-label" style={{ width: `${100 / hourLabels.length}%` }}>
                      {label}
                    </span>
                  ))}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const venue = venues.find((v) => v.id === row.venueId);
              const court = venue?.courts?.find((c) => c.id === row.courtId);
              const availabilitySlots = court?.availabilitySlots || [];
              const segments = segmentsForRow(
                row,
                date,
                availabilitySlots,
                blocks,
                bookings,
                matchesWithTournamentName,
                matchDurationMins
              );
              return (
                <tr key={`${row.venueId}-${row.courtId || idx}`} className="venue-gantt-row">
                  <td className="venue-gantt-row-label">
                    <div className="venue-gantt-row-label-venue">{row.venueName}</div>
                    <div className="venue-gantt-row-label-court">{row.courtName}</div>
                  </td>
                  <td colSpan={hourLabels.length} className="venue-gantt-timeline-cell">
                    <div className="venue-gantt-timeline">
                      {segments.map((seg, i) => (
                        <div
                          key={seg.id || i}
                          className={`venue-gantt-segment venue-gantt-segment-${seg.type}`}
                          style={{ left: `${seg.left}%`, width: `${Math.max(seg.width, 2)}%` }}
                          title={seg.label + (seg.matchNumber != null ? ` #${seg.matchNumber}` : '')}
                        >
                          <span className="venue-gantt-segment-label">{seg.label}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
